import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { ActKc3ActsKc2SelectionItem } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import { formatDateToLocale, getCheckedIdsArr } from 'utils';

import type { ColDef, ColGroupDef, IRowNode } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import {
    uerActKC3ActsKC2ActKC2CostGroupFilterGql,
    uerActKC3ActsKC2ActKC2DateFilterGql,
    uerActKC3ActsKC2ActKC2NumberFilterGql,
    uerActKC3ActsKC2ConstructionTypeIdFilterGql,
    uerActKC3ActsKC2ContractObjectNameFilterGql,
    uerActKC3ActsKC2WorkOrderInternalNumberFilterGql,
} from '../addOrEditActKc3Queries';

import { MenuCellRenderer, NumberAndDateCellRenderer } from './cellRenderers';
import { getActNumberAndDate } from './useAddOrEditActsKC3DataFilter';

const columnWidth = 146;

interface IUseWorkOrderActsKC2Columns {
    isReadonly?: boolean;
    isCorrection?: boolean;
    contractId: number;
    actKC3Id: number | undefined;
    rootContractObjectId?: number;
    workOrderId?: number;
    isFinal?: boolean;
}

export const useAddOrEditActsKC3Columns = (props: IUseWorkOrderActsKC2Columns) => {
    const { isReadonly, contractId, actKC3Id, rootContractObjectId, workOrderId, isFinal } = props;
    const { getConstructionTypeName } = useConstructionTypesListItems();

    const [checkedRows, setCheckedRows] = useState<Record<number, boolean>>({});
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const checkedRowsIds = useMemo(() => getCheckedIdsArr(checkedRows), [checkedRows]);

    const onCheck = useCallback(({ id, checked }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: checked }));
    }, []);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevState) => ({
            ...prevState,
            [fieldName]: checkedValues,
        }));
    }, []);

    const filterVariables = useMemo(
        () => ({
            contractId,
            actKC3Id,
            rootContractObjectId,
            workOrderId,
        }),
        [actKC3Id, contractId, rootContractObjectId, workOrderId],
    );

    const columns = useMemo<ColDef<ActKc3ActsKc2SelectionItem>[] | ColGroupDef<ActKc3ActsKc2SelectionItem>[]>(
        () => [
            ...(!isFinal && !!actKC3Id
                ? []
                : [
                      {
                          field: 'menu' as any,
                          headerName: '',
                          resizable: false,
                          width: 60,
                          cellRenderer: MenuCellRenderer({ onCheck, checkedRows, isReadonly }),
                          cellClass: 'center-aligned-cell',
                          headerClass: 'center-aligned-header',
                      },
                  ]),
            {
                field: 'workOrderInternalNumber',
                headerName: 'Номер НЗ',
                valueFormatter: (params) => {
                    const { data } = params;

                    if (!data) {
                        return '';
                    }

                    return `№${data?.workOrderInternalNumber} от ${formatDateToLocale(data?.workOrderDate)}`;
                },
                width: 185,
                sortable: true,
                comparator: (valueA, valueB) => valueA?.localeCompare(valueB),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2WorkOrderInternalNumberFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Номер НЗ',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                            valueFormatter: ({ value }: { value: string }) => `№${value}`,
                        },
                    ],
                    query: uerActKC3ActsKC2WorkOrderInternalNumberFilterGql,
                },
            },
            {
                headerName: 'Номер акта',
                field: 'actKC2FullNumber',
                width: 230,
                valueFormatter: ({ data }) => getActNumberAndDate(data),
                cellRenderer: NumberAndDateCellRenderer,
                sortable: true,
                comparator: (
                    valueA: any,
                    valueB: any,
                    nodeA: IRowNode<ActKc3ActsKc2SelectionItem>,
                    nodeB: IRowNode<ActKc3ActsKc2SelectionItem>,
                ) => {
                    const actA = getActNumberAndDate(nodeA?.data);
                    const actB = getActNumberAndDate(nodeB?.data);

                    return actA.localeCompare(actB);
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2ActKC2NumberFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Номер акта',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                            valueFormatter: ({ value }: { value: string }) => `№${value}`,
                        },
                    ],
                    query: uerActKC3ActsKC2ActKC2NumberFilterGql,
                },
            },
            {
                field: 'actKC2Date',
                headerName: 'Дата акта',
                width: 125,
                valueFormatter: (params) => formatDateToLocale(params.value),
                sortable: true,
                comparator: (valueA, valueB) => valueA?.localeCompare(valueB),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2ActKC2DateFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Дата акта',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                            valueFormatter: ({ data }: { data: { value: string } }) => formatDateToLocale(data.value),
                        },
                    ],
                    query: uerActKC3ActsKC2ActKC2DateFilterGql,
                },
            },
            {
                headerName: 'Отчетный период',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Начала',
                        field: 'actKC2StartDate',
                        valueFormatter: (params) => formatDateToLocale(params.value),
                        width: 120,
                    },
                    {
                        headerName: 'Окончание',
                        field: 'actKC2FinishDate',
                        valueFormatter: (params) => formatDateToLocale(params.value),
                        width: 120,
                    },
                ],
            },
            {
                headerName: 'Группа затрат',
                field: 'actKC2CostGroup',
                width: columnWidth,
                valueFormatter: ({ data }) => {
                    if (!data) {
                        return '';
                    }

                    return data.actKC2CostGroup === CostGroupEnum.Cmr ? 'СМР' : 'Прочие';
                },
                sortable: true,
                comparator: (valueA, valueB) => valueA?.localeCompare(valueB),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2ActKC2CostGroupFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Группа затрат',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                            valueFormatter: ({ data }: { data: { value: CostGroupEnum } }) =>
                                data.value === CostGroupEnum.Cmr ? 'СМР' : 'Прочие',
                        },
                    ],
                    query: uerActKC3ActsKC2ActKC2CostGroupFilterGql,
                },
            },
            {
                headerName: 'Вид стр-ва',
                field: 'constructionTypeId',
                width: columnWidth,
                valueFormatter: ({ data }) => {
                    if (!data) {
                        return '';
                    }

                    return data.constructionTypeShortName;
                },
                sortable: true,
                comparator: (valueA, valueB) => String(valueA)?.localeCompare(String(valueB)),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2ConstructionTypeIdFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Вид стр-ва',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                            valueFormatter: ({ value }: { value: number }) => getConstructionTypeName(value),
                        },
                    ],
                    query: uerActKC3ActsKC2ConstructionTypeIdFilterGql,
                },
            },
            {
                headerName: 'Объект',
                field: 'contractObjectName',
                width: columnWidth,
                flex: 1,
                valueFormatter: ({ data }) => {
                    if (!data) {
                        return '';
                    }

                    return data.contractObjectName;
                },
                sortable: true,
                comparator: (valueA, valueB) => valueA?.localeCompare(valueB),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    dataAccessor: 'uerActKC3ActsKC2ContractObjectNameFilter',
                    changeFilterHandler,
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Объект',
                            field: 'value',
                            checkboxSelection: true,
                            width: 398,
                        },
                    ],
                    query: uerActKC3ActsKC2ContractObjectNameFilterGql,
                },
            },
        ],
        [
            actKC3Id,
            changeFilterHandler,
            checkedRows,
            filterVariables,
            getConstructionTypeName,
            isFinal,
            isReadonly,
            onCheck,
        ],
    );

    return { columns, checkedRowsIds, setCheckedRows, filterValues };
};
